import React from 'react';
import { Route } from 'react-router-dom';
import { apiDocsPlugin, ApiExplorerPage } from '@backstage/plugin-api-docs';
import {
  CatalogEntityPage,
  CatalogIndexPage,
  catalogPlugin,
} from '@backstage/plugin-catalog';
import {
  CatalogImportPage,
  catalogImportPlugin,
} from '@backstage/plugin-catalog-import';
import { ScaffolderPage, scaffolderPlugin } from '@backstage/plugin-scaffolder';
import { orgPlugin } from '@backstage/plugin-org';
import { SearchPage } from '@backstage/plugin-search';
import {
  TechDocsIndexPage,
  techdocsPlugin,
  TechDocsReaderPage,
} from '@backstage/plugin-techdocs';
import { TechDocsAddons } from '@backstage/plugin-techdocs-react';
import { ReportIssue } from '@backstage/plugin-techdocs-module-addons-contrib';
import { UserSettingsPage } from '@backstage/plugin-user-settings';
import { NotificationsPage } from '@backstage/plugin-notifications';
import { apis } from './apis';
import { entityPage } from './components/catalog/EntityPage';
import { searchPage } from './components/search/SearchPage';
import { Root } from './components/Root';
import { microsoftAuthApiRef } from '@backstage/core-plugin-api'

import { SignInPage } from '@backstage/core-components';
import { createApp } from '@backstage/frontend-defaults';
import {
  convertLegacyAppOptions,
  convertLegacyAppRoot,
} from '@backstage/core-compat-api';
import {
  AppRootElementBlueprint,
  createFrontendModule,
} from '@backstage/frontend-plugin-api';
import authPlugin from '@backstage/plugin-auth';
import { AppRouter, FlatRoutes } from '@backstage/core-app-api';
import { CatalogGraphPage } from '@backstage/plugin-catalog-graph';
import { RequirePermission } from '@backstage/plugin-permission-react';
import { catalogEntityCreatePermission } from '@backstage/plugin-catalog-common/alpha';

import {HomepageCompositionRoot } from '@backstage/plugin-home'
import { DefaultTemplate } from './components/home/HomePage'
import { WhatslotPage } from '@internal/backstage-plugin-whatslot';
import { DoraMetricsPage } from '@internal/backstage-plugin-dora-metrics';
import { UnifiedThemeProvider } from '@backstage/theme';
import { speedyTheme } from './theme/theme';
import { NewRelicBrowserAgent } from './newRelicBrowserAgent';

// Hybrid New Frontend System setup: the legacy options and route tree below
// are converted into NFS features, which lets us install NFS-only plugins
// (e.g. @backstage/plugin-auth, which provides the OAuth consent page that
// MCP clients authenticating via CIMD are sent to).
const convertedOptionsModule = convertLegacyAppOptions({
  apis,
  components: {
    SignInPage: props => (
      <SignInPage
      {...props}
      auto
      provider={{
        id: 'microsoft-auth-provider',
        title: "Microsoft",
        message: "Sign in using Microsoft",
        apiRef: microsoftAuthApiRef,
      }}
      />
    ),
  },
  themes: [{
    id: 'speedy-theme',
    title: 'Speedyfreight Theme',
    variant: 'light',
    Provider: ({ children }) => (
      <UnifiedThemeProvider theme={speedyTheme}>{children}</UnifiedThemeProvider>
    ),
  }]
});

// convertLegacyAppRoot only keeps what's inside <AppRouter>, so app-wide
// side-effect components have to be registered as root elements instead.
const appRootElementsModule = createFrontendModule({
  pluginId: 'app',
  extensions: [
    AppRootElementBlueprint.make({
      name: 'newrelic-browser-agent',
      params: { element: <NewRelicBrowserAgent /> },
    }),
  ],
});

const routes = (
  <FlatRoutes>
    <Route path="/" element={<HomepageCompositionRoot />}>
      <DefaultTemplate />
    </Route>  
    <Route
      path="/catalog"
      element={<CatalogIndexPage initiallySelectedFilter="all" />}
    />
    <Route
      path="/catalog/:namespace/:kind/:name"
      element={<CatalogEntityPage />}
    >
      {entityPage}
    </Route>
    <Route path="/docs" element={<TechDocsIndexPage />} />
    <Route
      path="/docs/:namespace/:kind/:name/*"
      element={<TechDocsReaderPage />}
    >
      <TechDocsAddons>
        <ReportIssue />
      </TechDocsAddons>
    </Route>
    <Route path="/create" element={<ScaffolderPage />} />
    <Route
      path="/api-docs"
      element={<ApiExplorerPage initiallySelectedFilter="all" />}
    />
    <Route
      path="/catalog-import"
      element={
        <RequirePermission permission={catalogEntityCreatePermission}>
          <CatalogImportPage />
        </RequirePermission>
      }
    />
    <Route path="/search" element={<SearchPage />}>
      {searchPage}
    </Route>
    <Route path="/settings" element={<UserSettingsPage />} />
    <Route path="/notifications" element={<NotificationsPage />} />
    <Route path="/catalog-graph" element={<CatalogGraphPage />} />
    <Route path="/whatslot" element={<WhatslotPage />} />
    <Route path="/dora-metrics" element={<DoraMetricsPage />} />
  </FlatRoutes>
);

const convertedRootFeatures = convertLegacyAppRoot(
  <AppRouter>
    <Root>{routes}</Root>
  </AppRouter>,
);

const app = createApp({
  features: [
    convertedOptionsModule,
    appRootElementsModule,
    authPlugin,
    ...convertedRootFeatures,
  ],
  bindRoutes({ bind }) {
    bind(catalogPlugin.externalRoutes, {
      createComponent: scaffolderPlugin.routes.root,
      viewTechDoc: techdocsPlugin.routes.docRoot,
      createFromTemplate: scaffolderPlugin.routes.selectedTemplate,
    });
    bind(apiDocsPlugin.externalRoutes, {
      registerApi: catalogImportPlugin.routes.importPage,
    });
    bind(scaffolderPlugin.externalRoutes, {
      registerComponent: catalogImportPlugin.routes.importPage,
      viewTechDoc: techdocsPlugin.routes.docRoot,
    });
    bind(orgPlugin.externalRoutes, {
      catalogIndex: catalogPlugin.routes.catalogIndex,
    });
  },
});

export default app.createRoot();
